import { useAuth0 } from "@auth0/auth0-react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { CalendarDateTime, fromDate, getLocalTimeZone } from "@internationalized/date";
import { type FC, useEffect, useMemo, useState } from "react";
import type { DateValue } from "react-aria";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";

import { type PageMetadata } from "@moment/api-collab/api-types";
import { DatePicker } from "@moment/design-system";
import { InformationIcon } from "@moment/icons";
import {
	DAILY_NOTES_DOCUMENT_TYPE,
	type UserWithMembership,
	buildFullSlugPath,
	useDocumentCollabInfo,
	useDocumentMetadata,
	useRouteParams,
} from "@moment/state";
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tooltip,
	TooltipContent,
	TooltipPortal,
	TooltipProvider,
	TooltipTrigger,
	useConfirmDialog,
} from "@moment/ui";

import { AuthorSelect } from "../../common/AuthorSelect";
import { useReplaceCanvasRoute } from "../../navigation/hooks";

/** Consistent field wrapper for disabled/enabled states */
const FieldWrapper: FC<{
	children: React.ReactNode;
	disabled?: boolean;
}> = ({ children, disabled }) => <div className={disabled ? "opacity-60" : ""}>{children}</div>;

// Reserved page IDs normalize to lowercase with hyphens removed.
const normalizeReservedPageId = (id: string): string => id.toLowerCase().replaceAll("-", "");
const isReservedPageId = (id: string): boolean => normalizeReservedPageId(id) === "momentagentsmd";

// Form schema factory - accepts uniqueness checkers so validation lives in one place
const createPagePropertiesSchema = (context: {
	pageId: string;
	parentPageID: string | null;
	isPageIdUnique: (id: string, currentPageId: string) => boolean;
	isTitleUnique: (title: string, pageId: string) => boolean;
	isSlugUnique: (slug: string, pageId: string, parentPageID: string | null) => boolean;
}) =>
	z.object({
		title: z.string().refine((title) => context.isTitleUnique(title, context.pageId), {
			message: "Page titles must be unique within this document",
		}),
		id: z
			.string()
			.min(1, "Page ID is required")
			.refine((id) => !isReservedPageId(id), {
				message: "This ID is reserved",
			})
			.regex(/^[a-zA-Z0-9-]+$/, "Page ID can only contain letters, numbers, and hyphens")
			.refine((id) => context.isPageIdUnique(id, context.pageId), {
				message: "A page with this ID already exists in this document",
			}),
		slug: z
			.string()
			.min(1, "URL slug is required")
			.regex(/^[a-zA-Z0-9_-]+$/, {
				message: "Slug can only contain letters, numbers, hyphens, and underscores",
			})
			.refine((slug) => context.isSlugUnique(slug, context.pageId, context.parentPageID), {
				message: "A page with this slug already exists",
			}),
		authorID: z.string().optional(),
		publishedOn: z.date().optional(),
	});

type PagePropertiesFormData = z.infer<ReturnType<typeof createPagePropertiesSchema>>;

export interface PagePropertiesDialogProps {
	pageId: string;
	documentId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const PagePropertiesDialog: FC<PagePropertiesDialogProps> = ({
	pageId,
	documentId,
	open,
	onOpenChange,
}) => {
	const {
		metadata,
		updatePageMetadata,
		deletePage,
		isPageIdUnique,
		isPageTitleUnique,
		isSlugUnique,
	} = useDocumentMetadata(documentId);
	const collabInfo = useDocumentCollabInfo(documentId);
	const isCollab = !!collabInfo;
	const { user, isAuthenticated } = useAuth0();
	const { confirm } = useConfirmDialog();
	const { workspaceName } = useRouteParams();
	const replaceCanvasRoute = useReplaceCanvasRoute();

	// Create author list from current user if authenticated
	const authors: UserWithMembership[] = useMemo(() => {
		if (!isAuthenticated || !user) {
			return [];
		}
		return [
			{
				user_id: user.sub ?? "",
				name: user.name ?? "",
				nickname: user.nickname ?? "",
				picture: user.picture ?? "",
				relation: "owner" as const,
			},
		];
	}, [isAuthenticated, user]);

	// Find the current page
	const page = useMemo(
		() => metadata?.pagesMetadata?.find((p) => p.id === pageId),
		[metadata?.pagesMetadata, pageId]
	);

	// Check if this is a Daily Notes document (only title can be edited)
	const isDailyNotes = metadata?.type === DAILY_NOTES_DOCUMENT_TYPE;
	const isIndexPage = page?.slug === "index";

	// Track if slug should sync with ID
	const [slugSyncsWithId, setSlugSyncsWithId] = useState(false);
	const [idWarningAcknowledged, setIdWarningAcknowledged] = useState(false);
	const [idFocused, setIdFocused] = useState(false);

	// DatePicker state (managed separately due to CalendarDateTime complexity)
	const [publishedOn, setPublishedOn] = useState<CalendarDateTime | undefined>();

	// Build schema with uniqueness checks baked in
	const schema = useMemo(
		() =>
			createPagePropertiesSchema({
				pageId,
				parentPageID: page?.parentPageID ?? null,
				isPageIdUnique,
				isTitleUnique: isPageTitleUnique,
				isSlugUnique,
			}),
		[pageId, page?.parentPageID, isPageIdUnique, isPageTitleUnique, isSlugUnique]
	);

	const form = useForm<PagePropertiesFormData>({
		resolver: standardSchemaResolver(schema),
		mode: "onChange",
		defaultValues: {
			title: "",
			id: "",
			slug: "",
			authorID: undefined,
			publishedOn: undefined,
		},
	});

	const {
		formState: { errors, isDirty },
		watch,
		setValue,
		reset,
	} = form;

	const currentId = watch("id");

	// Initialize form when page changes or dialog opens
	useEffect(() => {
		if (page && open) {
			reset({
				title: page.title,
				id: page.id,
				slug: page.slug,
				authorID: page.authorID,
				publishedOn: page.publishedOn ? new Date(page.publishedOn) : undefined,
			});

			// Set DatePicker state
			if (page.publishedOn) {
				const date = new Date(page.publishedOn);
				const zonedDate = fromDate(date, getLocalTimeZone());
				setPublishedOn(
					new CalendarDateTime(
						zonedDate.year,
						zonedDate.month,
						zonedDate.day,
						zonedDate.hour,
						zonedDate.minute
					)
				);
			} else {
				setPublishedOn(undefined);
			}

			setIdWarningAcknowledged(false);
			setIdFocused(false);
			setSlugSyncsWithId(page.slug === page.id);
		}
	}, [page, open, reset]);

	// Sync slug with ID when they should be synced
	const handleIdChange = (value: string) => {
		setValue("id", value, { shouldValidate: true, shouldDirty: true });
		if (slugSyncsWithId) {
			setValue("slug", value, { shouldValidate: true, shouldDirty: true });
		}
	};
	const handleSlugChange = (value: string) => {
		setValue("slug", value, { shouldValidate: true, shouldDirty: true });
		if (value !== currentId) {
			setSlugSyncsWithId(false);
		}
	};

	// Handle DatePicker changes
	const handleDateChange = (value: DateValue) => {
		if ("hour" in value) {
			const newDate = new CalendarDateTime(
				value.year,
				value.month,
				value.day,
				value.hour,
				value.minute
			);
			setPublishedOn(newDate);
			setValue("publishedOn", newDate.toDate(getLocalTimeZone()), { shouldDirty: true });
		} else {
			const newDate = new CalendarDateTime(value.year, value.month, value.day, 0, 0);
			setPublishedOn(newDate);
			setValue("publishedOn", newDate.toDate(getLocalTimeZone()), { shouldDirty: true });
		}
	};

	const setPublishDateNow = () => {
		const now = fromDate(new Date(), getLocalTimeZone());
		const newDate = new CalendarDateTime(now.year, now.month, now.day, now.hour, now.minute);
		setPublishedOn(newDate);
		setValue("publishedOn", newDate.toDate(getLocalTimeZone()), { shouldDirty: true });
	};

	const clearPublishDate = () => {
		setPublishedOn(undefined);
		setValue("publishedOn", undefined, { shouldDirty: true });
	};

	// Check if publishedOn has changed (for dirty tracking)
	const publishedOnChanged = useMemo(() => {
		const newPublishedOn = publishedOn?.toDate(getLocalTimeZone());
		const oldPublishedOn = page?.publishedOn ? new Date(page.publishedOn) : undefined;
		return newPublishedOn?.toISOString() !== oldPublishedOn?.toISOString();
	}, [publishedOn, page?.publishedOn]);

	const hasChanges = isDirty || publishedOnChanged;
	const hasErrors = Object.keys(errors).length > 0;

	const handleSave = async (data: PagePropertiesFormData) => {
		if (!page) return;

		// For Daily Notes, only save title changes
		if (isDailyNotes) {
			if (data.title !== page.title) {
				await updatePageMetadata(pageId, { title: data.title });
			}
			onOpenChange(false);
			return;
		}

		if (data.id !== page.id && !idWarningAcknowledged) {
			const confirmed = await confirm({
				headingTitle: "Change Page ID?",
				content: (
					<>
						Changing the page ID from <strong>{page.id}</strong> to{" "}
						<strong>{data.id}</strong> may break existing links and references to this
						page. Are you sure you want to continue?
					</>
				),
				confirmLabel: "Change ID",
				variant: "negative",
			});
			if (!confirmed) return;
			setIdWarningAcknowledged(true);
		}

		const updates: Partial<PageMetadata> = {};

		if (data.title !== page.title) updates.title = data.title;
		if (data.id !== page.id) updates.id = data.id;
		if (data.slug !== page.slug) updates.slug = data.slug;
		if (data.authorID !== page.authorID) updates.authorID = data.authorID;

		const newPublishedOn = publishedOn?.toDate(getLocalTimeZone());
		const oldPublishedOn = page.publishedOn ? new Date(page.publishedOn) : undefined;
		if (newPublishedOn?.toISOString() !== oldPublishedOn?.toISOString()) {
			updates.publishedOn = newPublishedOn;
		}

		if (Object.keys(updates).length > 0) {
			await updatePageMetadata(pageId, updates);
		}

		onOpenChange(false);

		// If the page ID or slug changed, navigate to the updated slug path
		if (("id" in updates || "slug" in updates) && metadata?.pagesMetadata) {
			const updatedPagesMetadata = metadata.pagesMetadata.map((p) =>
				p.id === pageId ? { ...p, ...updates } : p
			);
			const updatedPage = updatedPagesMetadata.find((p) => p.id === (updates.id ?? pageId));
			if (updatedPage) {
				const newSlugPath = buildFullSlugPath(updatedPagesMetadata, updatedPage);
				replaceCanvasRoute({
					workspaceName,
					pageSlug: newSlugPath,
					version: "",
					type: "CanvasById",
					id: documentId,
				});
			}
		}
	};

	if (!page) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Page Options</DialogTitle>
				</DialogHeader>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSave)}
						className="flex flex-col gap-4 py-2"
					>
						{/* Daily Notes info banner */}
						{isDailyNotes && (
							<div className="flex items-start gap-2 rounded-md bg-palette-brand-3 p-3 text-sm text-palette-brand-11">
								<InformationIcon className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									This is a Daily Notes page. Only the title can be edited. Other
									properties are managed automatically.
								</span>
							</div>
						)}

						{/* Title */}
						<FormField
							control={form.control}
							name="title"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Title</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Page title" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Author */}
						{!isDailyNotes && (
							<FieldWrapper disabled={!isAuthenticated}>
								<FormField
									control={form.control}
									name="authorID"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Author</FormLabel>
											<FormControl>
												<AuthorSelect
													value={field.value}
													onChange={field.onChange}
													members={authors}
													placeholder={
														isAuthenticated
															? "Select author..."
															: "Log in to set author"
													}
													disabled={!isAuthenticated}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</FieldWrapper>
						)}

						{/* Publish Date */}
						{!isDailyNotes && (
							<FormItem>
								<FormLabel>Publish Date</FormLabel>
								<div className="flex items-center gap-1">
									<div className="min-w-0 flex-1 [&>div]:w-full [&>div]:flex [&>div>div]:justify-between">
										<DatePicker
											key={publishedOn?.toString() ?? "empty"}
											value={publishedOn}
											onChange={handleDateChange}
											granularity="minute"
											aria-label="Publish date"
											size="md"
											shouldCloseOnSelect
										/>
									</div>
									<Button
										type="button"
										preset="tertiary"
										size="md"
										onClick={setPublishDateNow}
										className="shrink-0"
									>
										Now
									</Button>
									<Button
										type="button"
										preset="tertiary"
										size="md"
										onClick={clearPublishDate}
										disabled={!publishedOn}
										className="shrink-0"
									>
										Clear
									</Button>
								</div>
							</FormItem>
						)}

						{/* Slug */}
						<FieldWrapper disabled={isDailyNotes || isIndexPage}>
							<FormField
								control={form.control}
								name="slug"
								render={({ field }) => (
									<FormItem>
										<FormLabel>URL Slug</FormLabel>
										{isIndexPage ? (
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<div>
															<FormControl>
																<Input
																	{...field}
																	placeholder="page-slug"
																	disabled
																/>
															</FormControl>
														</div>
													</TooltipTrigger>
													<TooltipPortal>
														<TooltipContent side="bottom">
															The first page always uses the
															&quot;index&quot; slug
														</TooltipContent>
													</TooltipPortal>
												</Tooltip>
											</TooltipProvider>
										) : (
											<FormControl>
												<Input
													{...field}
													onChange={(e) =>
														handleSlugChange(e.target.value)
													}
													placeholder="page-slug"
													color={errors.slug ? "negative" : "brand"}
													disabled={isDailyNotes}
												/>
											</FormControl>
										)}
										<FormMessage />
									</FormItem>
								)}
							/>
						</FieldWrapper>

						{/* Slug */}
						<FieldWrapper disabled={isDailyNotes || isIndexPage}>
							<FormField
								control={form.control}
								name="slug"
								render={({ field }) => (
									<FormItem>
										<FormLabel>URL Slug</FormLabel>
										{isIndexPage ? (
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<div>
															<FormControl>
																<Input
																	{...field}
																	placeholder="page-slug"
																	disabled
																/>
															</FormControl>
														</div>
													</TooltipTrigger>
													<TooltipPortal>
														<TooltipContent side="bottom">
															The first page always uses the
															&quot;index&quot; slug
														</TooltipContent>
													</TooltipPortal>
												</Tooltip>
											</TooltipProvider>
										) : (
											<FormControl>
												<Input
													{...field}
													onChange={(e) =>
														handleSlugChange(e.target.value)
													}
													placeholder="page-slug"
													color={errors.slug ? "negative" : "brand"}
													disabled={isDailyNotes}
												/>
											</FormControl>
										)}
										<FormMessage />
										{!isDailyNotes && !isIndexPage && !errors.slug && (
											<FormDescription className="!mt-0 text-xs">
												The URL-friendly identifier for this page
											</FormDescription>
										)}
									</FormItem>
								)}
							/>
						</FieldWrapper>

						{/* Page ID - disabled in collab mode (Reboot doesn't support key changes) */}
						<FieldWrapper disabled={isDailyNotes || isCollab}>
							<FormField
								control={form.control}
								name="id"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Page ID</FormLabel>
										{isCollab ? (
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<div>
															<FormControl>
																<Input
																	{...field}
																	placeholder="page-id"
																	disabled
																/>
															</FormControl>
														</div>
													</TooltipTrigger>
													<TooltipPortal>
														<TooltipContent side="bottom">
															Page IDs cannot be changed in
															collaborative documents
														</TooltipContent>
													</TooltipPortal>
												</Tooltip>
											</TooltipProvider>
										) : (
											<Popover
												open={idFocused && !errors.id && !isDailyNotes}
											>
												<PopoverTrigger asChild>
													<div>
														<FormControl>
															<Input
																{...field}
																onChange={(e) =>
																	handleIdChange(e.target.value)
																}
																onFocus={() => setIdFocused(true)}
																onBlur={() => setIdFocused(false)}
																placeholder="page-id"
																color={
																	errors.id ? "negative" : "brand"
																}
																disabled={isDailyNotes}
															/>
														</FormControl>
													</div>
												</PopoverTrigger>
												<PopoverContent
													side="bottom"
													align="start"
													className="w-auto max-w-sm !p-2 !py-3"
													onOpenAutoFocus={(e) => e.preventDefault()}
												>
													<div className="flex items-start gap-2 text-sm text-palette-warning-11">
														<InformationIcon className="mt-0.5 h-4 w-4 shrink-0" />
														<span>
															Changing the page ID may break existing
															links and references to this page.
														</span>
													</div>
												</PopoverContent>
											</Popover>
										)}
										<FormMessage />
									</FormItem>
								)}
							/>
						</FieldWrapper>
					</form>
				</Form>

				<DialogFooter>
					<DialogClose asChild>
						<Button preset="secondary">Cancel</Button>
					</DialogClose>
					<Button
						preset="primary"
						onClick={form.handleSubmit(handleSave)}
						disabled={!hasChanges || hasErrors}
					>
						Save Changes
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
